import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useAuth from 'src/hooks/useAuth';
import ChatInfoContato from './ChatInfoContato';
import ChatInfoNotas from './ChatInfoNotas';
import ChatInfoNegocios from './ChatInfoNegocios';
import ChatInfoChamados from './ChatInfoChamados';
import ChatInfoTarefas from './ChatInfoTarefas';
import TarefaDialog from 'src/pages/dashboard/Tarefas/TarefaDialog';
import tarefasService from 'src/services/tarefasService';
import ChatMedia from './ChatMedia';
import v2_actionListaLeadsPorChat from 'src/actions/v2/crm/v2_actionListaLeadsPorChat';
import actionListaChamadosPorChat from 'src/actions/chamados/actionListaChamadosPorChat';
import actionListaTarefasPorChat from 'src/actions/tarefas/actionListaTarefasPorChat';
import useNovoChamado from 'src/hooks/useNovoChamado';
import NovoChamado from 'src/sections/@dashboard/tickets/ChamadosNovoChamado/NovoChamado';

import { getChatInfoByChatId, getChatsAtendimento, getListaTags } from 'src/redux/slices/chat';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import VerContatoForm from 'src/sections/@dashboard/cadastro/VerContatoForm';
import ChatInfoContatoForm from './ChatInfoContatoForm';
import ModalNovoContato from './Modals/ModalNovoContato';
import TagsComponent from 'src/components/tags/TagsComponent';
import v2_actionEditarChat from 'src/actions/v2/chats/v2_actionEditarChat';
import { Badge } from 'src/@/components/ui/badge';
import Iconify from 'src/components/Iconify';
import { fDateTime } from 'src/utils/formatTime';

// ----------------------------------------------------------------------

ChatInfoSidebar.propTypes = {
    chat: PropTypes.object.isRequired
};

export default function ChatInfoSidebar({ chat, source, messagesWpp, ...other }) {
    const { t } = useTranslation();
    const { user, cliente } = useAuth();
    const [showInfo, setShowInfo] = useState(true);
    const [listaTags, setListaTags] = useState([]);
    const [listaLeads, setListaLeads] = useState([]);
    const dispatch = useDispatch();

    const { enqueueSnackbar } = useSnackbar();
    const { listaRespostas } = useSelector((state) => state.chat);
    const [stateModalNovoContato, setStateModalNovoContato] = useState(false);

    const [expandedSections, setExpandedSections] = useState({
        chat: true,
        tags: false,
        contato: false,
        negocios: false,
        arquivos: false,
        notas: false,
        chamados: false,
        tarefas: false,
    });

    const [chamadosCount, setChamadosCount] = useState(0);
    const [refreshChamados, setRefreshChamados] = useState(0);
    const [tarefasCount, setTarefasCount] = useState(0);
    const [refreshTarefas, setRefreshTarefas] = useState(0);
    const [tarefaDialogOpen, setTarefaDialogOpen] = useState(false);
    const [tarefaSelecionada, setTarefaSelecionada] = useState(null);
    const [tarefaWorkflows, setTarefaWorkflows] = useState([]);
    const { novoChamadoProps, handleOpenNovoChamadoComChat } = useNovoChamado();

    const triggerRefreshChamados = () => setRefreshChamados(prev => prev + 1);
    const triggerRefreshTarefas = () => setRefreshTarefas(prev => prev + 1);

    useEffect(() => {
        if (tarefaDialogOpen && user?.clienteId && tarefaWorkflows.length === 0) {
            tarefasService.listarWorkflows(user.clienteId).then(res => {
                setTarefaWorkflows(res.data || []);
            }).catch(() => { });
        }
    }, [tarefaDialogOpen]);

    const handleSaveTarefa = async (data) => {
        data.chat_id = chat._id;
        if (tarefaSelecionada?._id) {
            await tarefasService.editar(tarefaSelecionada._id, data);
        } else {
            await tarefasService.criar(user.clienteId, data);
        }
        triggerRefreshTarefas();
    };

    const handleEditarContato = async (scope, refreshLista) => {
        try {
            let data = { scope }
            await v2_actionEditarChat(chat._id, data)
            dispatch(getChatInfoByChatId(chat._id, chat.funil_id, user.id, false));
            enqueueSnackbar(t('chat.info.contact.updateSuccess'), { variant: 'success', autoHideDuration: 3000, anchorOrigin: { vertical: 'top', horizontal: 'right' } });
            if (refreshLista) {
                dispatch(getChatsAtendimento(user.clienteId, user.id, user.setorId, 10, true));
            }
        } catch (error) {
            enqueueSnackbar(t('chat.info.contact.updateError'), { variant: 'error', autoHideDuration: 3000, anchorOrigin: { vertical: 'top', horizontal: 'right' } });
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    async function buscarLeads() {
        try {
            let querys = `?usuario_id=${user.id}&chat_id=${chat._id}`
            const response = await v2_actionListaLeadsPorChat(querys);
            setListaLeads(response.data.data)
        } catch (error) {
        }
    }

    useEffect(() => {
        buscarLeads();
        let querys = `?cliente_id=${user.clienteId}`
        dispatch(getListaTags(querys))
    }, [chat]);

    useEffect(() => {
        if (chat?._id) {
            actionListaChamadosPorChat(chat._id).then(res => {
                setChamadosCount((res.data?.data || []).length);
            }).catch(() => { });
            actionListaTarefasPorChat(chat._id).then(res => {
                setTarefasCount((res.data?.data || []).length);
            }).catch(() => { });
        }
    }, [chat?._id, refreshChamados, refreshTarefas]);

    return (
        <div className="min-w-[320px] h-full flex flex-col overflow-y-auto" {...other}>
            {/* Chat Info */}
            <div className="border-b border-gray-200 dark:border-[#2a2a2a]">
                <button
                    onClick={() => toggleSection('chat')}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        <Iconify icon="eva:message-circle-outline" width={18} className="text-gray-500 dark:text-gray-400" />
                        <span className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">{t('chat.info.sections.chat')}</span>
                    </div>
                    <Iconify
                        icon={expandedSections.chat ? "mdi:chevron-up" : "mdi:chevron-down"}
                        width={18}
                        className="text-gray-400"
                    />
                </button>

                {expandedSections.chat && (
                    <div className="px-3 pb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                        <ChatInfoContato
                            messagesWpp={messagesWpp}
                            cliente={cliente}
                            chat={chat}
                            listaTags={listaTags}
                            isCollapse={showInfo}
                            onCollapse={() => setShowInfo((prev) => !prev)}
                        />

                        <hr className="my-3 border-gray-200 dark:border-[#2a2a2a]" />
                        <div className="space-y-2.5">
                            {chat?.lastDateMsgClient && (
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('chat.info.chat.lastClientMessage')}</span>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{fDateTime(chat.lastDateMsgClient)}</span>
                                </div>
                            )}
                            {chat?.dataCriacao && (
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('chat.info.chat.createdAt')}</span>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{fDateTime(chat.dataCriacao)}</span>
                                </div>
                            )}
                            {chat?.lastUpdate && (
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('chat.info.chat.lastUpdate')}</span>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{fDateTime(chat.lastUpdate)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Tags */}
            <div className="border-b border-gray-200 dark:border-[#2a2a2a]">
                <button
                    onClick={() => toggleSection('tags')}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        <Iconify icon="eva:pricetags-outline" width={18} className="text-gray-500 dark:text-gray-400" />
                        <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">{t('chat.info.sections.tags')}</span>
                        {chat?.tags_ids?.length > 0 && (
                            <Badge
                                variant="secondary"
                                className="h-5 min-w-[20px] px-1.5 text-[11px] font-bold bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400"
                            >
                                {chat.tags_ids.length}
                            </Badge>
                        )}
                    </div>
                    <Iconify
                        icon={expandedSections.tags ? "mdi:chevron-up" : "mdi:chevron-down"}
                        width={18}
                        className="text-gray-400"
                    />
                </button>

                {expandedSections.tags && (
                    <div className="px-3 pb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                        <TagsComponent
                            user={user}
                            chat={chat}
                            chatId={chat?._id}
                            tags={chat?.tags_ids || []}
                        />
                    </div>
                )}
            </div>

            {/* Contato */}
            <div className="border-b border-gray-200 dark:border-[#2a2a2a]">
                <button
                    onClick={() => toggleSection('contato')}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        <Iconify icon="eva:person-outline" width={18} className="text-gray-500 dark:text-gray-400" />
                        <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">{t('chat.info.sections.contact')}</span>
                    </div>
                    <Iconify
                        icon={expandedSections.contato ? "mdi:chevron-up" : "mdi:chevron-down"}
                        width={18}
                        className="text-gray-400"
                    />
                </button>

                {expandedSections.contato && (
                    <div className="px-3 pb-4 space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                        <ChatInfoContatoForm
                            cliente={cliente}
                            lead={chat}
                            onOpenModalNovoContato={() => setStateModalNovoContato(true)}
                            onEditarContatoId={value => {
                                handleEditarContato({ contato_id: value })
                            }}
                        />

                        {chat?.contato && (
                            <VerContatoForm
                                callBackRefreshList={() => {
                                    try {
                                        dispatch(getChatInfoByChatId(chat._id))
                                        dispatch(getChatsAtendimento(user.clienteId, user.id, user.setorId, 100, false))
                                    } catch (error) {
                                        console.log(error)
                                    }
                                }}
                                contatoData={chat?.contato}
                                cliente={cliente}
                                showSaveButton={true}
                                hideSelectEmpresa={true}
                                showSignupButton={false}
                                loadingNovoContato={false}
                            />
                        )}

                        <ModalNovoContato
                            cliente={cliente}
                            user={user}
                            open={stateModalNovoContato}
                            listaRespostas={listaRespostas}
                            listaClientesCanais={[]}
                            callbackAddContact={data => {
                                handleEditarContato({ contato_id: data._id })
                            }}
                            onClose={() => setStateModalNovoContato(false)}
                        />
                    </div>
                )}
            </div>

            {/* Negócios */}
            {source !== 'funil.chat' && (
                <div className="border-b border-gray-200 dark:border-[#2a2a2a]">
                    <button
                        onClick={() => toggleSection('negocios')}
                        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                        <div className="flex items-center gap-2.5">
                            <Iconify icon="eva:briefcase-outline" width={18} className="text-gray-500 dark:text-gray-400" />
                            <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">{t('chat.info.sections.deals')}</span>
                            {listaLeads?.length > 0 && (
                                <Badge
                                    variant="secondary"
                                    className="h-5 min-w-[20px] px-1.5 text-[11px] font-bold bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-400"
                                >
                                    {listaLeads.length}
                                </Badge>
                            )}
                        </div>
                        <Iconify
                            icon={expandedSections.negocios ? "mdi:chevron-up" : "mdi:chevron-down"}
                            width={18}
                            className="text-gray-400"
                        />
                    </button>

                    {expandedSections.negocios && (
                        <div className="px-3 pb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                            <ChatInfoNegocios
                                chat={chat}
                                user={user}
                                cliente={cliente}
                                callbackRefresh={() => buscarLeads()}
                                listaLeads={listaLeads}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Arquivos */}
            <div className="border-b border-gray-200 dark:border-[#2a2a2a]">
                <button
                    onClick={() => toggleSection('arquivos')}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        <Iconify icon="eva:attach-outline" width={18} className="text-gray-500 dark:text-gray-400" />
                        <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">{t('chat.info.sections.files')}</span>
                        {messagesWpp?.filter(msg => msg.type === 'image' || msg.type === 'document').length > 0 && (
                            <Badge
                                variant="secondary"
                                className="h-5 min-w-[20px] px-1.5 text-[11px] font-bold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                            >
                                {messagesWpp.filter(msg => msg.type === 'image' || msg.type === 'document').length}
                            </Badge>
                        )}
                    </div>
                    <Iconify
                        icon={expandedSections.arquivos ? "mdi:chevron-up" : "mdi:chevron-down"}
                        width={18}
                        className="text-gray-400"
                    />
                </button>

                {expandedSections.arquivos && (
                    <div className="px-3 pb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                        <ChatMedia messages={messagesWpp} />
                    </div>
                )}
            </div>

            {/* Notas */}
            <div className="border-b border-gray-200 dark:border-[#2a2a2a]">
                <button
                    onClick={() => toggleSection('notas')}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        <Iconify icon="eva:file-text-outline" width={18} className="text-gray-500 dark:text-gray-400" />
                        <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">{t('chat.info.sections.notes')}</span>
                        {chat?.notas?.length > 0 && (
                            <Badge
                                variant="secondary"
                                className="h-5 min-w-[20px] px-1.5 text-[11px] font-bold bg-pink-100 dark:bg-pink-500/15 text-pink-700 dark:text-pink-400"
                            >
                                {chat.notas.length}
                            </Badge>
                        )}
                    </div>
                    <Iconify
                        icon={expandedSections.notas ? "mdi:chevron-up" : "mdi:chevron-down"}
                        width={18}
                        className="text-gray-400"
                    />
                </button>

                {expandedSections.notas && (
                    <div className="px-3 pb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                        <ChatInfoNotas
                            chat={chat}
                            user={user}
                        />
                    </div>
                )}
            </div>

            {/* Chamados */}
            <div className="border-b border-gray-200 dark:border-[#2a2a2a]">
                <button
                    onClick={() => toggleSection('chamados')}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        <Iconify icon="eva:clipboard-outline" width={18} className="text-gray-500 dark:text-gray-400" />
                        <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">{t('chat.info.sections.tickets')}</span>
                        {chamadosCount > 0 && (
                            <Badge
                                variant="secondary"
                                className="h-5 min-w-[20px] px-1.5 text-[11px] font-bold bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400"
                            >
                                {chamadosCount}
                            </Badge>
                        )}
                    </div>
                    <Iconify
                        icon={expandedSections.chamados ? "mdi:chevron-up" : "mdi:chevron-down"}
                        width={18}
                        className="text-gray-400"
                    />
                </button>

                {expandedSections.chamados && (
                    <div className="px-3 pb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                        <ChatInfoChamados
                            chat={chat}
                            user={user}
                            onOpenNovoChamado={() => handleOpenNovoChamadoComChat(chat._id, triggerRefreshChamados)}
                            onCountUpdate={(count) => setChamadosCount(count)}
                            refreshTrigger={refreshChamados}
                        />
                    </div>
                )}
            </div>

            {/* Tarefas */}
            <div className="border-b border-gray-200 dark:border-[#2a2a2a]">
                <button
                    onClick={() => toggleSection('tarefas')}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        <Iconify icon="eva:checkmark-circle-outline" width={18} className="text-gray-500 dark:text-gray-400" />
                        <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">{t('chat.info.sections.tasks')}</span>
                        {tarefasCount > 0 && (
                            <Badge
                                variant="secondary"
                                className="h-5 min-w-[20px] px-1.5 text-[11px] font-bold bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400"
                            >
                                {tarefasCount}
                            </Badge>
                        )}
                    </div>
                    <Iconify
                        icon={expandedSections.tarefas ? "mdi:chevron-up" : "mdi:chevron-down"}
                        width={18}
                        className="text-gray-400"
                    />
                </button>

                {expandedSections.tarefas && (
                    <div className="px-3 pb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                        <ChatInfoTarefas
                            chat={chat}
                            user={user}
                            onOpenNovaTarefa={() => {
                                setTarefaSelecionada(null);
                                setTarefaDialogOpen(true);
                            }}
                            onClickTarefa={(tarefa) => {
                                setTarefaSelecionada(tarefa);
                                setTarefaDialogOpen(true);
                            }}
                            onCountUpdate={(count) => setTarefasCount(count)}
                            refreshTrigger={refreshTarefas}
                        />
                    </div>
                )}
            </div>

            <NovoChamado {...novoChamadoProps} />

            <TarefaDialog
                open={tarefaDialogOpen}
                onClose={() => {
                    setTarefaDialogOpen(false);
                    setTarefaSelecionada(null);
                }}
                onSave={handleSaveTarefa}
                task={tarefaSelecionada}
                workflows={tarefaWorkflows}
                selectedWorkflowId={tarefaSelecionada?.workflow_id?._id || tarefaWorkflows[0]?._id || ''}
            />
        </div>
    );
}
